/**
 * Schematic Service
 * Handles API communication for schematic builder and batch-first workflow
 */

import { Observable } from 'rxjs'
import { environment } from 'src/environments/environment'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'

import {
    AddToQueueRequest, BatchQueueEntry, BatchRun, CompleteBatchRunRequest, CreateConnectionRequest, EquipmentConnection, InterruptBatchRunRequest,
    NextStepSuggestion, ProcessFlowData, ReorderQueueRequest, SavePositionsRequest, SchematicData, StartBatchRunRequest, StationSchematicData,
    StationEquipmentLinkData, UpdateConnectionRequest
} from '../models/schematic.model'

@Injectable({
    providedIn: 'root'
})
export class SchematicService {

    private readonly API_BASE = environment.api;

    constructor(
        private http: HttpClient
    ) { }

    // ==========================================
    // Schematic Data
    // ==========================================

    /**
     * Get full schematic data for a site
     * Includes equipment, connections, positions, and active batches
     */
    getSchematic(siteId: string): Observable<SchematicData> {
        return this.http.get<SchematicData>(`${this.API_BASE}/oee/schematic/${siteId}`);
    }

    /**
     * Save equipment positions (bulk update)
     */
    savePositions(request: SavePositionsRequest): Observable<void> {
        return this.http.put<void>(
            `${this.API_BASE}/oee/schematic/${request.site_id}/positions`,
            request
        );
    }

    // ==========================================
    // Station Schematic
    // ==========================================

    /**
     * Get full station schematic data for a site
     */
    getStationSchematic(siteId: string): Observable<StationSchematicData> {
        return this.http.get<StationSchematicData>(`${this.API_BASE}/ops/station-schematic/${siteId}`);
    }

    getProcessFlow(siteId: string): Observable<ProcessFlowData> {
        return this.http.get<ProcessFlowData>(`${this.API_BASE}/ops/process-flow/${siteId}`);
    }

    /**
     * Save station positions (bulk upsert)
     */
    saveStationPositions(siteId: string, positions: { station_id: string; x: number; y: number; width: number; height: number }[]): Observable<{ success: boolean }> {
        return this.http.put<{ success: boolean }>(
            `${this.API_BASE}/ops/station-schematic/${siteId}/positions`,
            { positions }
        );
    }

    createStationEquipmentLink(stationId: string, equipmentId: string): Observable<StationEquipmentLinkData> {
        return this.http.post<StationEquipmentLinkData>(
            `${this.API_BASE}/ops/station-equipment-link`,
            { station_id: stationId, equipment_id: equipmentId }
        );
    }

    deleteStationEquipmentLink(linkId: string): Observable<void> {
        return this.http.delete<void>(`${this.API_BASE}/ops/station-equipment-link/${linkId}`);
    }

    // ==========================================
    // Equipment Connections
    // ==========================================

    /**
     * Create a new connection between equipment
     */
    createConnection(request: CreateConnectionRequest): Observable<EquipmentConnection> {
        return this.http.post<EquipmentConnection>(
            `${this.API_BASE}/oee/connections`,
            request
        );
    }

    /**
     * Update a connection (label, type, sides)
     */
    updateConnection(connectionId: string, updates: UpdateConnectionRequest): Observable<EquipmentConnection> {
        return this.http.patch<EquipmentConnection>(
            `${this.API_BASE}/oee/connections/${connectionId}`,
            updates
        );
    }

    /**
     * Delete a connection
     */
    deleteConnection(connectionId: string): Observable<void> {
        return this.http.delete<void>(
            `${this.API_BASE}/oee/connections/${connectionId}`
        );
    }

    // ==========================================
    // Batch Runs
    // ==========================================

    /**
     * Get all runs for a batch (journey history)
     */
    getBatchRuns(batchId: string): Observable<BatchRun[]> {
        return this.http.get<BatchRun[]>(
            `${this.API_BASE}/oee/batch-runs/${batchId}`
        );
    }

    /**
     * Start a batch run on equipment
     */
    startBatchRun(request: StartBatchRunRequest): Observable<BatchRun> {
        return this.http.post<BatchRun>(
            `${this.API_BASE}/oee/batch-runs/start`,
            request
        );
    }

    /**
     * Complete a batch run
     */
    completeBatchRun(request: CompleteBatchRunRequest): Observable<BatchRun> {
        return this.http.post<BatchRun>(
            `${this.API_BASE}/oee/batch-runs/${request.run_id}/complete`,
            request
        );
    }

    /**
     * Interrupt a batch run
     */
    interruptBatchRun(request: InterruptBatchRunRequest): Observable<BatchRun> {
        return this.http.post<BatchRun>(
            `${this.API_BASE}/oee/batch-runs/${request.run_id}/interrupt`,
            request
        );
    }

    // ==========================================
    // Batch Queue
    // ==========================================

    /**
     * Get batch queue for equipment
     */
    getQueue(equipmentId: string): Observable<BatchQueueEntry[]> {
        return this.http.get<BatchQueueEntry[]>(
            `${this.API_BASE}/oee/queue/${equipmentId}`
        );
    }

    /**
     * Add batch to queue
     */
    addToQueue(request: AddToQueueRequest): Observable<BatchQueueEntry> {
        return this.http.post<BatchQueueEntry>(
            `${this.API_BASE}/oee/queue/${request.equipment_id}/add`,
            request
        );
    }

    /**
     * Reorder batch queue
     */
    reorderQueue(request: ReorderQueueRequest): Observable<void> {
        return this.http.put<void>(
            `${this.API_BASE}/oee/queue/${request.equipment_id}/reorder`,
            request
        );
    }

    /**
     * Remove batch from queue
     */
    removeFromQueue(equipmentId: string, batchId: string): Observable<void> {
        return this.http.delete<void>(
            `${this.API_BASE}/oee/queue/${equipmentId}/${batchId}`
        );
    }

    // ==========================================
    // Next Step Suggestions
    // ==========================================

    /**
     * Get suggested next equipment for a batch
     */
    getNextStep(batchId: string): Observable<NextStepSuggestion> {
        return this.http.get<NextStepSuggestion>(
            `${this.API_BASE}/oee/next-step/${batchId}`
        );
    }
}
